<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import AppSidebar from '@/components/AppSidebar.vue';
import ConfirmHost from '@/components/ConfirmHost.vue';
import ConsentBanner from '@/components/ConsentBanner.vue';
import { isNativeApp } from '@/lib/native';
import AppLockScreen from '@/components/AppLockScreen.vue';
import UserActions from '@/components/UserActions.vue';

const auth = useAuthStore();
const route = useRoute();

// Ni menu ni barre d'application sur les pages publiques (connexion, accueil).
// L'expert-comptable a son propre en-tête (AccountantView), sans le menu de l'entreprise.
const showNav = computed(() => Boolean(auth.user) && !route.meta.public && auth.user?.kind !== 'accountant');
const isLanding = computed(() => Boolean(route.meta.landing));
</script>

<template>
  <!-- L'accueil occupe toute la largeur, hors du gabarit de l'application. -->
  <div v-if="isLanding">
    <RouterView />
    <ConfirmHost />
    <ConsentBanner v-if="!isNativeApp" />
    <AppLockScreen v-if="isNativeApp" />
  </div>
  <div v-else class="flex min-h-screen">
    <AppSidebar v-if="showNav" />
    <div class="min-w-0 flex-1">
      <!-- Barre du haut, desktop uniquement : en mobile, ces actions sont dans
           la barre fixe d'AppSidebar. -->
      <div v-if="showNav" class="mx-auto hidden max-w-6xl justify-end px-4 pt-4 sm:flex">
        <UserActions />
      </div>
      <!-- `pt-20` compense la barre mobile en `fixed` (cf. AppSidebar) ; ne
           s'applique pas sur la page de connexion, qui n'a pas de sidebar. -->
      <!-- Compte de démonstration : rien ne part sur le réseau réel ni par vrai email. -->
      <div
        v-if="showNav && auth.user?.environment === 'sandbox'"
        class="mx-auto mt-20 max-w-6xl px-4 sm:mt-2"
        role="status"
      >
        <p class="rounded-lg border border-amber-300 bg-amber-100 px-3 py-2 text-sm text-amber-800">
          <strong>Compte de démonstration</strong> — factures transmises au bac à sable (sans valeur légale), emails
          reçus dans la boîte de test Mailtrap.
        </p>
      </div>
      <main class="safe-main mx-auto max-w-6xl px-4 pb-6 sm:pt-4" :class="showNav ? 'pt-20' : 'pt-6'">
        <RouterView />
      </main>
    </div>
    <ConfirmHost />
    <ConsentBanner v-if="!isNativeApp" />
    <AppLockScreen v-if="isNativeApp" />
  </div>
</template>
